<script setup lang="ts">
import {
  Building2,
  ChevronsUpDown,
  Landmark,
  Languages,
  Monitor,
  Scale,
  ScrollText,
} from "lucide-vue-next";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, RouterView } from "vue-router";
import Menu from "primevue/menu";
import type { MenuItem } from "primevue/menuitem";
import { useSession } from "@/features/auth";
import { BreakGlassBanner, HOME } from "@/shared";
import { useBreakGlass } from "./break-glass";
import type { Locale } from "./i18n";
import { WORKSPACE_PATH, router, visibleWorkspaces } from "./router";
import type { Workspace } from "@/shared";

/**
 * One glyph per workspace, chosen from the room's own vocabulary: a kiosk is a machine on the
 * floor, the cage is the bank inside the building, the day closes on a balance — a scale — tenancy
 * is the building itself, and the activity trail is the ledger roll the room's actions are written
 * onto. Icons appear in the rail and nowhere else; the workspaces keep their text labels, which are
 * what the tests and the operators read.
 */
const WORKSPACE_ICON: Record<Workspace, typeof Monitor> = {
  kiosks: Monitor,
  cage: Landmark,
  day: Scale,
  tenancy: Building2,
  activity: ScrollText,
};

/** An engraved-plate English micro-label riding beside each Thai nav label — the "instrument"
 *  voice the Trachang system uses for every mono caption. Not translated: it is the same on both
 *  locales, a fixed part of the chrome like a stamped plate. */
const WORKSPACE_MICRO: Record<Workspace, string> = {
  kiosks: "KIOSKS",
  cage: "CAGE",
  day: "DAY",
  tenancy: "ADMIN",
  activity: "LOG",
};

/**
 * The chrome every workspace sits inside.
 *
 * **Identity is permanent** — name, role and Casino are always on screen, never behind a menu.
 * On a personal machine the question "who am I signed in as, and at which Casino" has to be
 * answerable without clicking, because the answer decides whether the next action is allowed and
 * whose id it lands against. The `Menu` beside the identity cluster holds the two controls that
 * are safe to hide — language and sign-out — and nothing that answers "who am I". A test in
 * `AppShell.test.ts` was written before this header existed specifically to keep that line where
 * it is.
 *
 * **Navigation hides what a role can never reach and disables nothing.** That is the opposite of
 * the rule for actions, deliberately: two of the role splits are counterintuitive (a technician
 * authorizes an operation but cannot close a stranded one; a manager cannot run one but signs off
 * the day), so a disabled *action* with its reason teaches the model, while a permanently dead
 * *section* just looks broken.
 *
 * Nothing here reads a query. Identity, role and break-glass all come from Pinia, so the header
 * cannot end up asserting an all-clear over a failed fetch the way a summary figure can.
 */
const { t, locale } = useI18n();
const session = useSession();
const breakGlass = useBreakGlass();

/**
 * Only the workspaces this role can actually open, in nav order. `visibleWorkspaces` (in
 * `router.ts`) is the same table the route guard uses, narrowed for `platform_admin` so the cash
 * workspaces stay out of the permanent nav until break-glass is active — see that function's
 * comment for why. Kept out of this file so the rule is testable without mounting the shell.
 */
const workspaces = computed(() => {
  const role = session.role;
  if (!role) return [];
  return visibleWorkspaces(role, breakGlass.isActive).map((w) => ({
    key: w,
    path: WORKSPACE_PATH[w],
    label: t(`workspace.${w}`),
    micro: WORKSPACE_MICRO[w],
    icon: WORKSPACE_ICON[w],
  }));
});

/** The avatar disc: an identifier fragment, so it stays in the mono face like the username. */
const initials = computed(() => session.staff?.username.slice(0, 2).toUpperCase() ?? null);

/** `null` for exactly one role — platform_admin, which is scoped to no Casino. */
const casinoLabel = computed(() => session.casinoId ?? t("shell.allCasinos"));

/**
 * The `lang` attribute is what activates the `:lang(th)` leading rule in `tokens.css` — Thai stacks
 * vowels and tone marks vertically and collides with itself at Latin line height. `immediate` so
 * the first paint is right; setting it only on change leaves the initial Thai render too tight.
 */
watch(
  locale,
  (next) => {
    document.documentElement.lang = next;
  },
  { immediate: true },
);

function setLocale(next: Locale): void {
  locale.value = next;
}

/**
 * Clears the store, the module-level token AND every cached query (`session.logout()`, see
 * `features/auth/session.ts`), THEN explicitly navigates to `/login`.
 *
 * **The navigation is not optional and not implicit.** `router.beforeEach` fires on a navigation;
 * `session.logout()` is a Pinia mutation, not one, so nothing runs and nothing moves without this
 * `router.replace` — `docs/UNOBSERVED.md` §1.2 is exactly what this screen looked like before that
 * call existed: the URL stayed on the workspace, the report stayed rendered, and every control that
 * could have led back to `/login` had just been removed from the header. `replace`, not `push`: the
 * workspace being left should not be one `back` tap away — the same rule `leaveBreakGlass` below
 * follows.
 */
function signOut(): void {
  session.logout();
  void router.replace("/login");
}

/**
 * Both halves, or the admin is stranded. Clearing break-glass alone drops the workspace from the
 * nav and the banner from the screen, but `canEnter` still admits `platform_admin` everywhere and
 * the route never moves — so admin sits on the exact screen they can no longer legitimately use,
 * and every scoped call it makes from here on omits `casinoId` and gets a `400`. `replace`, not
 * `push`: the workspace they are leaving should not be one `back` tap away.
 */
function leaveBreakGlass(): void {
  breakGlass.leave();
  const role = session.role;
  if (role) router.replace(WORKSPACE_PATH[HOME[role]]);
}

/**
 * The two controls that are safe to hide, and only those.
 *
 * **The active locale is NOT `disabled`.** `Menu.findNextOptionIndex` selects the next arrow-key
 * target with `li[data-pc-section="item"][data-p-disabled="false"]`, so a disabled item is skipped
 * by ArrowDown and never becomes `aria-activedescendant` — a keyboard or screen-reader operator
 * would walk ไทย → Sign out and never meet the language they are currently in. Browse mode is worse
 * than silent: it announces "dimmed, unavailable", which reads as "this language is not offered".
 * The current locale is marked with `aria-checked` instead (see `menuPassThrough`), which is what
 * "current" actually means, and re-picking it is a harmless no-op.
 *
 * `locale` on each language item is not a PrimeVue field — it is the marker `menuPassThrough` reads
 * to tell a language row from Sign out.
 *
 * Sign-out is `visible` only when signed in — the header renders on `/login` too, where the
 * language pair still has to be reachable and there is no session to end.
 */
const menuItems = computed<MenuItem[]>(() => [
  {
    label: t("shell.language"),
    items: (["th", "en"] as Locale[]).map((l) => ({
      label: t(`shell.${l}`),
      locale: l,
      command: () => setLocale(l),
    })),
  },
  { separator: true, visible: session.isAuthed },
  { label: t("shell.signOut"), visible: session.isAuthed, command: signOut },
]);

/**
 * Radio semantics on the two language rows, and nothing on any other row.
 *
 * `Menuitem` builds its `<li>` as `mergeProps({ role: "menuitem", … }, getPTOptions("item"))`, so a
 * pass-through wins over the hardcoded role — no `#item` template, no custom markup, four lines.
 * "Two of these, one is on" is exactly `menuitemradio` + `aria-checked`, and it is the only
 * announcement that distinguishes the current language from an unavailable one.
 *
 * The inline `style` rides along because the overlay is teleported to `document.body`: scoped CSS
 * in this file cannot reach it, and `:deep()` cannot either once the node is outside this
 * component's subtree. Weight is the whole visual treatment — the check state is carried by ARIA.
 */
const menuPassThrough = computed(() => ({
  item: ({ context }: { context: { item: MenuItem } }) => {
    const itemLocale = context.item.locale as Locale | undefined;
    if (!itemLocale) return {};
    const current = locale.value === itemLocale;
    return {
      role: "menuitemradio",
      "aria-checked": current,
      style: current ? { fontWeight: 600 } : undefined,
    };
  },
}));

const menu = ref<InstanceType<typeof Menu> | null>(null);
const menuOpen = ref(false);
</script>

<template>
  <!-- Signed out — the login door. No rail: an empty 232px column beside a sign-in card is chrome
       for a workspace the visitor cannot reach yet. Only the language control survives, top-right,
       because `/login` is bilingual and a bounced-mid-shift operator still needs to switch. The
       same `aria-haspopup="menu"` trigger and the same `<Menu>` (language rows; sign-out hidden by
       `visible: session.isAuthed`) as the authed rail — `session-lifecycle.test.ts` §1.2 reopens
       exactly this menu after logout. -->
  <div v-if="!session.isAuthed" class="login-shell">
    <a class="skip" href="#main">{{ t("shell.skipToContent") }}</a>

    <!-- Registration marks in the corners of the whole viewport — the sheet the login card is
         printed on. Decorative; the sign-in card carries the real branding. -->
    <span class="sheetmark tl" aria-hidden="true"></span>
    <span class="sheetmark tr" aria-hidden="true"></span>
    <span class="sheetmark bl" aria-hidden="true"></span>
    <span class="sheetmark br" aria-hidden="true"></span>

    <header class="login-bar">
      <button
        class="langbtn"
        type="button"
        :aria-label="t('shell.menu')"
        aria-haspopup="menu"
        :aria-expanded="menuOpen"
        @click="menu?.toggle($event)"
      >
        <Languages :size="16" aria-hidden="true" />
        <span>{{ t(`shell.${locale}`) }}</span>
      </button>
    </header>

    <main id="main" class="login-main">
      <RouterView />
    </main>

    <Menu
      ref="menu"
      :model="menuItems"
      :popup="true"
      :pt="menuPassThrough"
      :aria-label="t('shell.menu')"
      @show="menuOpen = true"
      @hide="menuOpen = false"
    />
  </div>

  <div v-else class="shell">
    <a class="skip" href="#main">{{ t("shell.skipToContent") }}</a>

    <aside class="rail">
      <div class="brand">
        <span class="brandmark" aria-hidden="true"><Scale :size="20" /></span>
        <span class="brandtext">{{ t("shell.title") }}</span>
      </div>

      <nav class="nav" :aria-label="t('shell.workspaces')">
        <p class="nav-eyebrow">{{ t("shell.workspaces") }}</p>
        <RouterLink v-for="w in workspaces" :key="w.key" :to="w.path" class="tab">
          <component :is="w.icon" class="tab-icon" :size="18" aria-hidden="true" />
          <span class="tab-label">{{ w.label }}</span>
          <span class="tab-micro" aria-hidden="true">{{ w.micro }}</span>
        </RouterLink>
      </nav>

      <!-- Identity, pinned to the rail's bottom and permanently visible — the button opens the
           menu, but everything it says is readable without opening anything. The menu holds only
           the two controls safe to hide: language and sign-out. -->
      <div class="identity">
        <button
          class="menubtn"
          type="button"
          :aria-label="t('shell.menu')"
          aria-haspopup="menu"
          :aria-expanded="menuOpen"
          @click="menu?.toggle($event)"
        >
          <span class="avatar" aria-hidden="true">
            <template v-if="initials">{{ initials }}</template>
            <Languages v-else :size="16" />
          </span>

          <span class="who" v-if="session.staff">
            <!-- Username is an identifier: never translated, and rendered in the mono face so it
                 can be read aloud and matched against a log line. -->
            <span class="user">{{ session.staff.username }}</span>
            <span class="meta">
              {{ t(`role.${session.staff.role}`) }} · <span class="casino">{{ casinoLabel }}</span>
            </span>
          </span>

          <ChevronsUpDown v-if="session.staff" class="chev" :size="14" aria-hidden="true" />
        </button>

        <Menu
          ref="menu"
          :model="menuItems"
          :popup="true"
          :pt="menuPassThrough"
          :aria-label="t('shell.menu')"
          @show="menuOpen = true"
          @hide="menuOpen = false"
        />
      </div>
    </aside>

    <div class="frame">
      <!-- Above the workspace and undismissable: the banner is ADR-0005's attribution trade said
           out loud, and it is only worth saying while it applies. -->
      <BreakGlassBanner
        v-if="breakGlass.casino"
        class="breakglass-slot"
        :casino="breakGlass.casino"
        @leave="leaveBreakGlass"
      />

      <main id="main" class="main">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<style scoped>
/* The login door: a bare full-height page, brand and language top, the sign-in card centering
   itself in the main. No rail. */
.login-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
/* The four corner marks on the viewport sheet — brass brackets, faint, straddling the page edge. */
.sheetmark {
  position: fixed;
  width: 18px;
  height: 18px;
  border: 2px solid rgb(201 165 87 / 0.35);
  pointer-events: none;
  z-index: 1;
}
.sheetmark.tl {
  top: 18px;
  left: 18px;
  border-right: 0;
  border-bottom: 0;
}
.sheetmark.tr {
  top: 18px;
  right: 18px;
  border-left: 0;
  border-bottom: 0;
}
.sheetmark.bl {
  bottom: 18px;
  left: 18px;
  border-right: 0;
  border-top: 0;
}
.sheetmark.br {
  bottom: 18px;
  right: 18px;
  border-left: 0;
  border-top: 0;
}
@media (max-width: 900px) {
  .sheetmark {
    display: none;
  }
}
.login-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--s4);
  padding: var(--s4) var(--s5);
}
.login-main {
  flex: 1;
  display: flex;
  padding: 0 var(--s5);
}
.langbtn {
  font: var(--t-small);
  font-weight: 550;
  display: inline-flex;
  align-items: center;
  gap: var(--s2);
  padding: var(--s2) var(--s3);
  border: 1px solid var(--rule-2);
  border-radius: var(--r-full);
  background: transparent;
  color: var(--ink-2);
  cursor: pointer;
}
.langbtn:hover {
  background: var(--sunken);
  color: var(--ink);
}
.langbtn:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.shell {
  min-height: 100vh;
  display: flex;
}

/* Visible only once focused — a keyboard user reaching the workspace without tabbing the whole
   nav, which on the operations table is the difference between usable and not. */
.skip {
  position: absolute;
  left: -9999px;
  z-index: 20;
}
.skip:focus {
  position: fixed;
  inset-inline-start: var(--s3);
  top: var(--s3);
  padding: var(--s3);
  background: var(--surface);
  border: 1px solid var(--rule-2);
  border-radius: var(--r-sm);
}

/* The rail: navigation and identity in one still column. Sticky and viewport-tall, because which
   row of a long report an operator is on never changes who they are signed in as. Its own surface,
   a shade off the workspace ground, with a hairline seam. */
.rail {
  width: 240px;
  flex-shrink: 0;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: var(--s5);
  padding: var(--s4) var(--s3) var(--s3);
  background: var(--surface);
  border-inline-end: var(--e1-border);
}

/* Brand lockup: the brass scale mark and the wordmark. The mark grounds the app in the counting
   room without spending the guilloche, which is reserved for the certification moments. */
.brand {
  display: flex;
  align-items: center;
  gap: var(--s2);
  padding: var(--s1) var(--s2);
}
.brandmark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: var(--r-sm);
  background: var(--accent-subtle);
  color: var(--accent);
  border: 1px solid rgb(201 165 87 / 0.3);
}
.brandtext {
  font: var(--t-title);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
/* An eyebrow that names what the list is — sentence case, letter-spaced, quiet. */
.nav-eyebrow {
  margin: 0 0 var(--s1);
  padding: 0 var(--s2);
  font: var(--t-micro);
  letter-spacing: 0.06em;
  color: var(--ink-3);
}
:lang(th) .nav-eyebrow {
  letter-spacing: 0;
}

/* The active item borrows the tile's edge language: the rail is a tile whose intent is
   "you are here". unplugin-vue-router keeps vue-router's default active classes. */
.tab {
  display: flex;
  align-items: center;
  gap: var(--s3);
  height: 40px;
  padding: 0 var(--s3);
  border-radius: var(--r-sm);
  color: var(--ink-2);
  font-weight: 550;
  text-decoration: none;
  white-space: nowrap;
}
.tab:hover {
  background: var(--sunken);
  color: var(--ink);
}
.tab.router-link-active {
  background: var(--accent-subtle);
  color: var(--accent);
  box-shadow: inset var(--edge) 0 0 var(--accent);
}
.tab:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}
.tab-icon {
  flex-shrink: 0;
}
.tab-label {
  flex: 1;
}
/* The engraved plate: a mono English label, quiet, right-aligned — lights to brass when active. */
.tab-micro {
  font: var(--t-micro);
  font-family: var(--mono);
  letter-spacing: 0.08em;
  color: var(--ink-3);
}
.tab.router-link-active .tab-micro {
  color: var(--accent);
}

.identity {
  margin-top: auto;
}

/* The identity sits in its own bordered card at the rail's foot — a mini profile, not a loose row.
   The whole card is the menu trigger, and everything it says is readable without opening anything:
   the button is a door, not a hiding place. */
.menubtn {
  font: inherit;
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--s2);
  padding: var(--s2);
  border: 1px solid var(--rule);
  border-radius: var(--r-md);
  background: var(--ground);
  color: var(--ink);
  cursor: pointer;
  text-align: start;
}
.menubtn:hover {
  background: var(--sunken);
  border-color: var(--rule-2);
}
.menubtn[aria-expanded="true"] {
  border-color: var(--accent);
}
.menubtn:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.avatar {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: var(--r-full);
  background: var(--accent-subtle);
  color: var(--accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: 600 13px var(--mono);
}

.who {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.user {
  font-family: var(--mono);
  font-size: var(--fs-small);
  font-weight: 550;
  overflow: hidden;
  text-overflow: ellipsis;
}
.meta {
  font-size: var(--fs-micro);
  color: var(--ink-3);
}
.casino {
  font-family: var(--mono);
}

.chev {
  flex-shrink: 0;
  color: var(--ink-3);
}

.frame {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.breakglass-slot {
  margin: var(--s4) var(--s5) 0;
}

.main {
  flex: 1;
  padding: var(--s5) var(--s5) var(--s7);
}

/* Narrow terminals: the rail folds to glyphs. Identity collapses to the avatar disc — still on
   screen, still the trigger — and the workspace labels yield to their icons. */
@media (max-width: 899px) {
  .rail {
    width: 64px;
    padding: var(--s4) var(--s2);
    align-items: center;
  }
  .brand,
  .tab-label,
  .tab-micro,
  .who,
  .chev {
    display: none;
  }
  .nav {
    width: 100%;
  }
  .tab {
    justify-content: center;
    padding: 0;
  }
  .menubtn {
    justify-content: center;
    padding: var(--s1);
  }
  .main {
    padding: var(--s4);
  }
}
</style>
